import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { materialTheme } from '@/lib/material-design-tokens';

interface MaterialDashboardGridProps {
  children: React.ReactNode;
  className?: string;
}

// Animation variants for the container
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
      duration: 0.6,
      ease: materialTheme.motion.curves.decelerate,
    },
  },
};

// Animation variants for individual grid items
const itemVariants = {
  hidden: { 
    opacity: 0, 
    y: 20,
    scale: 0.95,
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: materialTheme.motion.curves.standard,
    },
  },
};

export const MaterialDashboardGrid: React.FC<MaterialDashboardGridProps> = ({
  children,
  className,
}) => {
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className={cn(
        // Container styling
        'w-full',
        'p-6',
        'space-y-6',
        className
      )}
      style={{
        backgroundColor: materialTheme.colors.surface.background,
      }}
    >
      {children}
    </motion.div>
  );
};

// Metric Cards Grid (4 cards in a row)
interface MetricCardsGridProps {
  children: React.ReactNode;
  className?: string;
}

export const MetricCardsGrid: React.FC<MetricCardsGridProps> = ({
  children,
  className,
}) => {
  return (
    <motion.div
      variants={itemVariants}
      className={cn(
        // Grid layout - auto-fit to fill available width
        'grid gap-6',
        'grid-cols-1',
        'sm:grid-cols-2', 
        'lg:grid-cols-4',
        // Responsive spacing
        'w-full',
        className
      )}
    >
      {children}
    </motion.div>
  );
};

// Charts Grid (3 charts in a row)
interface ChartsGridProps {
  children: React.ReactNode;
  className?: string;
}

export const ChartsGrid: React.FC<ChartsGridProps> = ({
  children,
  className,
}) => {
  return (
    <motion.div
      variants={itemVariants}
      className={cn(
        // Grid layout
        'grid grid-cols-1 gap-6',
        'md:grid-cols-2',
        'lg:grid-cols-3',
        // Responsive spacing
        'w-full',
        className
      )}
    >
      {children}
    </motion.div>
  );
};

// Data Section Grid (Table + Quick Actions)
interface DataSectionGridProps {
  children: React.ReactNode;
  className?: string;
}

export const DataSectionGrid: React.FC<DataSectionGridProps> = ({
  children,
  className,
}) => {
  return (
    <motion.div
      variants={itemVariants}
      className={cn(
        // Grid layout
        'grid grid-cols-1 gap-6',
        'lg:grid-cols-3',
        // Responsive spacing
        'w-full',
        className
      )}
    >
      {children}
    </motion.div>
  );
};

// Material Section Container
interface MaterialSectionProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  className?: string;
}

export const MaterialSection: React.FC<MaterialSectionProps> = ({
  children,
  title,
  subtitle,
  action,
  className,
}) => {
  return (
    <motion.section
      variants={itemVariants}
      className={cn('w-full space-y-4', className)}
    >
      {(title || subtitle || action) && (
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            {title && (
              <h2 
                className="text-2xl font-semibold tracking-tight text-gray-900 text-left"
                style={materialTheme.typography.headlineSmall}
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <p 
                className="text-gray-600"
                style={materialTheme.typography.bodyMedium}
              >
                {subtitle}
              </p>
            )}
          </div>
          {action && (
            <div className="flex-shrink-0">
              {action}
            </div>
          )}
        </div>
      )}
      {children}
    </motion.section>
  );
};

// Material Card Container (base for all dashboard cards)
interface MaterialCardProps {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  clickable?: boolean;
  onClick?: () => void;
  elevation?: 1 | 2 | 3 | 4 | 5;
}

export const MaterialCard: React.FC<MaterialCardProps> = ({
  children,
  className,
  hover = false,
  clickable = false,
  onClick,
  elevation = 1,
}) => {
  const elevationClass = `shadow-${elevation === 1 ? 'sm' : elevation === 2 ? 'md' : 
    elevation === 3 ? 'lg' : elevation === 4 ? 'xl' : '2xl'}`;

  return (
    <motion.div
      variants={itemVariants}
      className={cn(
        // Base card styling
        'bg-white rounded-2xl w-full',
        'border border-gray-100',
        elevationClass,
        // Interactive states
        hover && 'transition-all duration-300 ease-out',
        hover && 'hover:shadow-lg hover:-translate-y-1',
        clickable && 'cursor-pointer',
        clickable && 'hover:shadow-xl',
        // Responsive padding - will be overridden if needed
        'p-6',
        className
      )}
      style={{
        borderRadius: materialTheme.borderRadius.xl,
        backgroundColor: materialTheme.colors.surface.surface,
      }}
      onClick={onClick}
      whileHover={hover ? { y: -2 } : undefined}
      whileTap={clickable ? { scale: 0.98 } : undefined}
    >
      {children}
    </motion.div>
  );
};

// Grid Item Wrapper (for consistent spacing)
interface GridItemProps {
  children: React.ReactNode;
  className?: string;
  span?: 1 | 2 | 3 | 4;
}

export const GridItem: React.FC<GridItemProps> = ({
  children,
  className,
  span = 1,
}) => {
  const spanClass = span === 1 ? 'col-span-1' : 
    span === 2 ? 'col-span-2' : 
    span === 3 ? 'col-span-3' : 'col-span-4';

  return (
    <motion.div
      variants={itemVariants}
      className={cn(spanClass, className)}
    >
      {children}
    </motion.div>
  );
};

// Export all components
export {
  MaterialDashboardGrid as default,
  itemVariants,
  containerVariants,
};